import { useMemo, useState } from "react";
import { ChevronDown, Crosshair, Search } from "lucide-react";
import { Button } from "~/components/ui/button";
import { Input } from "~/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "~/components/ui/popover";
import { PartialSkill } from "~/services/skills";

interface SkillsPopoverProps {
  skills: PartialSkill[];
  // Receives the slash-prefixed token (e.g. "/kaplan-meier") ready to insert
  // into the composer.
  onSelect: (skillToken: string) => void;
}

export const SkillsPopover = ({ skills, onSelect }: SkillsPopoverProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return skills;
    return skills.filter(
      (s) => s.name.toLowerCase().includes(q) || s.description.toLowerCase().includes(q)
    );
  }, [skills, query]);

  const handleSelect = (skill: PartialSkill) => {
    onSelect(`/${skill.name}`);
    setOpen(false);
    setQuery("");
  };

  return (
    <Popover
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery("");
      }}
    >
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 gap-1.5 px-2 text-xs text-muted-foreground"
        >
          <Crosshair className="size-3.5" />
          Skills
          <ChevronDown className="size-3.5" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-96 p-0">
        <div className="border-b px-3 py-2">
          <div className="relative">
            <Search className="absolute left-2 top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground" />
            <Input
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search skills…"
              className="h-8 pl-7 text-xs"
              aria-label="Search skills"
            />
          </div>
        </div>

        <ul className="flex max-h-80 flex-col overflow-y-auto py-1" role="list">
          {filtered.length === 0 ? (
            <li className="px-3 py-6 text-center text-xs text-muted-foreground">
              {skills.length === 0
                ? "No skills available for this conversation."
                : `No skills match “${query}”`}
            </li>
          ) : (
            filtered.map((skill) => (
              <li key={skill.id}>
                <button
                  type="button"
                  onClick={() => handleSelect(skill)}
                  className="flex w-full flex-col items-start gap-0.5 px-3 py-2 text-left hover:bg-muted focus-visible:bg-muted focus-visible:outline-none"
                >
                  <span className="font-mono text-sm font-medium text-foreground">
                    /{skill.name}
                  </span>
                  <span className="text-xs text-muted-foreground">{skill.description}</span>
                </button>
              </li>
            ))
          )}
        </ul>
      </PopoverContent>
    </Popover>
  );
};
